import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent, CardActionArea,
  Chip, LinearProgress, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Divider, Stack, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tab, Tabs, CircularProgress,
  InputAdornment, Badge, Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BalanceIcon from '@mui/icons-material/Balance';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportCajaChicaWorkbook } from '../utils/excel/cajaChicaWorkbook';

const CATEGORIAS = [
  'Útiles de oficina', 'Transporte / Movilidad', 'Alimentación',
  'Limpieza y mantenimiento', 'Comunicaciones', 'Materiales y suministros',
  'Servicios y reparaciones', 'Gastos bancarios', 'Otros gastos',
];

const TIPO_COLOR = {
  APERTURA:   { bg: '#e3f2fd', color: '#1565c0', label: 'Apertura' },
  EGRESO:     { bg: '#fce4ec', color: '#b71c1c', label: 'Egreso' },
  REPOSICION: { bg: '#e8f5e9', color: '#1b5e20', label: 'Reposición' },
  AJUSTE:     { bg: '#fff3e0', color: '#e65100', label: 'Ajuste' },
  CIERRE:     { bg: '#f3e5f5', color: '#4a148c', label: 'Cierre' },
};

const fmt = (v) => new Intl.NumberFormat('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0);
const fmtDate = (d) => d ? new Date(d + (d.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('es-PE') : '';
const today = () => new Date().toISOString().split('T')[0];

function SaldoBar({ saldo, fondo, umbral }) {
  const pct = fondo > 0 ? Math.min(100, (saldo / fondo) * 100) : 0;
  const bajo = umbral != null && saldo <= umbral;
  const color = bajo ? 'error' : pct >= 60 ? 'success' : pct >= 30 ? 'warning' : 'error';
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          S/ {fmt(saldo)} de S/ {fmt(fondo)}
        </Typography>
        <Typography variant="caption" color={bajo ? 'error.main' : 'text.secondary'} fontWeight={bajo ? 700 : 400}>
          {pct.toFixed(0)}%
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} color={color} sx={{ borderRadius: 4, height: 8 }} />
    </Box>
  );
}

function StatCard({ icon, label, value, color, sub }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: `${color}.light`, borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}.light`, display: 'flex' }}>
          {React.cloneElement(icon, { sx: { color: `${color}.dark`, fontSize: 28 } })}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} color={`${color}.dark`}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function CajaChicaPage() {
  const [cajas, setCajas] = useState([]);
  const [resumen, setResumen] = useState({ total_cajas: 0, cajas_activas: 0, total_saldo: 0, total_fondo: 0, alertas_reposicion: 0 });
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // Dialogs
  const [dlgCaja, setDlgCaja]     = useState({ open: false, modo: 'crear', data: {} });
  const [dlgEgreso, setDlgEgreso] = useState({ open: false });
  const [dlgReponer, setDlgReponer] = useState({ open: false });
  const [dlgArqueo, setDlgArqueo]  = useState({ open: false });
  const [dlgCerrar, setDlgCerrar]  = useState({ open: false });
  const [dlgDelMov, setDlgDelMov]  = useState({ open: false, mov: null });

  const [formCaja, setFormCaja]     = useState({ nombre: '', descripcion: '', moneda: 'PEN', fondo_asignado: '', responsable_nombre: '', umbral_reposicion: '', fecha_apertura: today() });
  const [formEgreso, setFormEgreso] = useState({ monto: '', concepto: '', categoria: '', nro_comprobante: '', proveedor: '', observaciones: '', fecha_movimiento: today() });
  const [formReponer, setFormReponer] = useState({ monto: '', concepto: 'Reposición de caja chica', observaciones: '', fecha_movimiento: today() });
  const [formArqueo, setFormArqueo]   = useState({ saldo_fisico: '', observaciones: '', fecha_arqueo: today() });
  const [filtroEstado, setFiltroEstado] = useState('ACTIVA');
  const [saving, setSaving] = useState(false);
  const [cajasPage, setCajasPage] = useState(0);
  const cajasPerPage = 6;

  const [dlgExport, setDlgExport]       = useState(false);
  const [exportForm, setExportForm]     = useState({ desde: today(), hasta: today(), cajaId: '', tipo: '' });
  const [exportLoading, setExportLoading] = useState(false);

  const toast = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const cargarResumen = useCallback(async () => {
    try {
      const r = await axios.get('/api/caja-chica/resumen');
      setResumen(r.data);
    } catch {}
  }, []);

  const cargarCajas = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstado !== 'TODAS') params.estado = filtroEstado;
      const r = await axios.get('/api/caja-chica', { params });
      setCajas(r.data);
      setCajasPage(0);
    } catch (e) {
      toast('Error cargando cajas chicas', 'error');
    } finally { setLoading(false); }
  }, [filtroEstado]);

  const cargarDetalle = useCallback(async (id) => {
    setLoadingDetalle(true);
    try {
      const r = await axios.get(`/api/caja-chica/${id}`);
      setSelected(r.data);
    } catch {
      toast('Error cargando detalle', 'error');
    } finally { setLoadingDetalle(false); }
  }, []);

  useEffect(() => { cargarResumen(); }, [cargarResumen]);
  useEffect(() => { cargarCajas(); if (selected) cargarDetalle(selected.id); }, [cargarCajas]);

  const handleSelectCaja = (caja) => {
    setSelected(caja);
    setTab(0);
    cargarDetalle(caja.id);
  };

  const handleGuardarCaja = async () => {
    setSaving(true);
    try {
      if (dlgCaja.modo === 'crear') {
        await axios.post('/api/caja-chica', formCaja);
        toast('Caja chica creada exitosamente');
      } else {
        await axios.put(`/api/caja-chica/${selected.id}`, formCaja);
        toast('Caja chica actualizada');
      }
      setDlgCaja({ open: false, modo: 'crear', data: {} });
      cargarCajas(); cargarResumen();
      if (dlgCaja.modo === 'editar' && selected) cargarDetalle(selected.id);
    } catch (e) {
      toast(e.response?.data?.error || 'Error guardando', 'error');
    } finally { setSaving(false); }
  };

  const handleMovimiento = async (tipo, form) => {
    setSaving(true);
    try {
      await axios.post(`/api/caja-chica/${selected.id}/movimiento`, { tipo, ...form });
      toast(tipo === 'EGRESO' ? 'Egreso registrado' : 'Reposición registrada');
      if (tipo === 'EGRESO') setDlgEgreso({ open: false });
      else setDlgReponer({ open: false });
      cargarDetalle(selected.id); cargarCajas(); cargarResumen();
    } catch (e) {
      toast(e.response?.data?.error || 'Error registrando movimiento', 'error');
    } finally { setSaving(false); }
  };

  const handleArqueo = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`/api/caja-chica/${selected.id}/arqueo`, formArqueo);
      const dif = Number(r.data.diferencia);
      toast(`Arqueo realizado${Math.abs(dif) > 0.009 ? `. Diferencia ajustada: S/ ${fmt(dif)}` : '. Sin diferencias'}`);
      setDlgArqueo({ open: false });
      cargarDetalle(selected.id); cargarCajas(); cargarResumen();
    } catch (e) {
      toast(e.response?.data?.error || 'Error en arqueo', 'error');
    } finally { setSaving(false); }
  };

  const handleCerrar = async () => {
    setSaving(true);
    try {
      await axios.post(`/api/caja-chica/${selected.id}/cerrar`, {});
      toast('Caja chica cerrada');
      setDlgCerrar({ open: false });
      setSelected(null);
      cargarCajas(); cargarResumen();
    } catch (e) {
      toast(e.response?.data?.error || 'Error cerrando caja', 'error');
    } finally { setSaving(false); }
  };

  const handleEliminarMov = async () => {
    try {
      await axios.delete(`/api/caja-chica/${selected.id}/movimiento/${dlgDelMov.mov.id}`);
      toast('Movimiento eliminado');
      setDlgDelMov({ open: false, mov: null });
      cargarDetalle(selected.id); cargarCajas(); cargarResumen();
    } catch (e) {
      toast(e.response?.data?.error || 'Error eliminando', 'error');
    }
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const params = { desde: exportForm.desde, hasta: exportForm.hasta };
      if (exportForm.cajaId) params.cajaId = exportForm.cajaId;
      if (exportForm.tipo)   params.tipo   = exportForm.tipo;
      const r = await axios.get('/api/caja-chica/reporte', { params });
      await exportCajaChicaWorkbook({
        cajas:       r.data.cajas,
        movimientos: r.data.movimientos,
        filtros:     r.data.filtros,
        usuario:     '',
      });
      setDlgExport(false);
      toast('Excel generado exitosamente');
    } catch (e) {
      toast(e.response?.data?.error || 'Error generando el reporte', 'error');
    } finally { setExportLoading(false); }
  };

  const abrirCrear = () => {
    setFormCaja({ nombre: '', descripcion: '', moneda: 'PEN', fondo_asignado: '', responsable_nombre: '', umbral_reposicion: '', fecha_apertura: today() });
    setDlgCaja({ open: true, modo: 'crear' });
  };

  const abrirEditar = () => {
    setFormCaja({
      nombre: selected.nombre || '',
      descripcion: selected.descripcion || '',
      moneda: selected.moneda || 'PEN',
      fondo_asignado: selected.fondo_asignado || '',
      responsable_nombre: selected.responsable_nombre || '',
      umbral_reposicion: selected.umbral_reposicion || '',
      fecha_apertura: selected.fecha_apertura ? selected.fecha_apertura.split('T')[0] : today(),
    });
    setDlgCaja({ open: true, modo: 'editar' });
  };

  const abrirEgreso = () => {
    setFormEgreso({ monto: '', concepto: '', categoria: '', nro_comprobante: '', proveedor: '', observaciones: '', fecha_movimiento: today() });
    setDlgEgreso({ open: true });
  };

  const abrirReponer = () => {
    setFormReponer({ monto: '', concepto: 'Reposición de caja chica', observaciones: '', fecha_movimiento: today() });
    setDlgReponer({ open: true });
  };

  const abrirArqueo = () => {
    setFormArqueo({ saldo_fisico: selected ? String(selected.saldo_actual) : '', observaciones: '', fecha_arqueo: today() });
    setDlgArqueo({ open: true });
  };

  const estadoCaja = (c) => {
    if (c.estado === 'CERRADA') return { label: 'Cerrada', color: 'default' };
    if (c.estado === 'INACTIVA') return { label: 'Inactiva', color: 'warning' };
    const bajo = c.umbral_reposicion != null && Number(c.saldo_actual) <= Number(c.umbral_reposicion);
    if (bajo) return { label: 'Reponer', color: 'error' };
    return { label: 'Activa', color: 'success' };
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LocalAtmIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Caja Chica</Typography>
            <Typography variant="body2" color="text.secondary">Fondos rotativos de efectivo</Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { cargarCajas(); cargarResumen(); }} size="small">
            Actualizar
          </Button>
          <Button variant="outlined" color="success" startIcon={<FileDownloadIcon />}
            onClick={() => { setExportForm({ desde: today(), hasta: today(), cajaId: '', tipo: '' }); setDlgExport(true); }} size="small">
            Exportar Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirCrear} size="small">
            Nueva Caja
          </Button>
        </Stack>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<AccountBalanceWalletIcon />} label="Cajas Activas" value={resumen.cajas_activas} color="primary" sub={`${resumen.total_cajas} total`} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<LocalAtmIcon />} label="Total en Caja" value={`S/ ${fmt(resumen.total_saldo)}`} color="success" sub={`Fondo: S/ ${fmt(resumen.total_fondo)}`} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<TrendingDownIcon />} label="Nivel Promedio"
            value={resumen.total_fondo > 0 ? `${((resumen.total_saldo / resumen.total_fondo) * 100).toFixed(0)}%` : '—'}
            color="info" sub="del fondo asignado" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<WarningAmberIcon />} label="Alertas Reposición"
            value={resumen.alertas_reposicion}
            color={resumen.alertas_reposicion > 0 ? 'error' : 'warning'}
            sub="fondos bajo umbral" />
        </Grid>
      </Grid>

      {/* Filtro */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        {['ACTIVA', 'CERRADA', 'TODAS'].map(e => (
          <Chip key={e} label={e === 'TODAS' ? 'Todas' : e === 'ACTIVA' ? 'Activas' : 'Cerradas'}
            onClick={() => setFiltroEstado(e)}
            color={filtroEstado === e ? 'primary' : 'default'}
            variant={filtroEstado === e ? 'filled' : 'outlined'}
            size="small" />
        ))}
      </Box>

      {/* Layout principal: lista + detalle */}
      <Grid container spacing={2} alignItems="flex-start">
        {/* Lista de cajas */}
        <Grid item xs={12} md={selected ? 5 : 12}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : cajas.length === 0 ? (
            <Paper elevation={0} sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 3, p: 6, textAlign: 'center' }}>
              <LocalAtmIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">No hay cajas chicas {filtroEstado !== 'TODAS' ? filtroEstado.toLowerCase() + 's' : ''}</Typography>
              <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={abrirCrear}>Crear primera caja</Button>
            </Paper>
          ) : (
            <>
            <Stack spacing={1.5}>
              {cajas.slice(cajasPage * cajasPerPage, (cajasPage + 1) * cajasPerPage).map(c => {
                const st = estadoCaja(c);
                const bajo = c.umbral_reposicion != null && Number(c.saldo_actual) <= Number(c.umbral_reposicion);
                const isSelected = selected?.id === c.id;
                return (
                  <Card key={c.id} elevation={isSelected ? 3 : 0}
                    sx={{ border: '1px solid', borderColor: isSelected ? 'primary.main' : bajo ? 'error.light' : 'divider',
                          borderRadius: 3, cursor: 'pointer', transition: 'all .2s',
                          '&:hover': { borderColor: 'primary.light', boxShadow: 3 } }}>
                    <CardActionArea onClick={() => handleSelectCaja(c)} sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight={700} noWrap>{c.nombre}</Typography>
                            {bajo && <WarningAmberIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                          </Box>
                          {c.responsable_nombre && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PersonIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                              <Typography variant="caption" color="text.secondary">{c.responsable_nombre}</Typography>
                            </Box>
                          )}
                        </Box>
                        <Chip label={st.label} color={st.color} size="small" />
                      </Box>
                      {c.estado !== 'CERRADA' && (
                        <SaldoBar saldo={Number(c.saldo_actual)} fondo={Number(c.fondo_asignado)} umbral={c.umbral_reposicion} />
                      )}
                      {c.estado === 'CERRADA' && (
                        <Typography variant="caption" color="text.disabled">
                          Cerrada el {fmtDate(c.fecha_cierre)}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" color="text.disabled">
                          {c.total_movimientos} movimientos
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          Abierta: {fmtDate(c.fecha_apertura)}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
            {cajas.length > cajasPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {`Mostrando ${cajasPage * cajasPerPage + 1}–${Math.min((cajasPage + 1) * cajasPerPage, cajas.length)} de ${cajas.length}`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" disabled={cajasPage === 0} onClick={() => setCajasPage(p => p - 1)}>Anterior</Button>
                  <Typography variant="caption" sx={{ px: 0.5 }}>
                    {cajasPage + 1} / {Math.ceil(cajas.length / cajasPerPage)}
                  </Typography>
                  <Button size="small" variant="outlined" disabled={(cajasPage + 1) * cajasPerPage >= cajas.length} onClick={() => setCajasPage(p => p + 1)}>Siguiente</Button>
                </Box>
              </Box>
            )}
            </>
          )}
        </Grid>

        {/* Panel de detalle */}
        {selected && (
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
              {/* Header detalle */}
              <Box sx={{ p: 2.5, bgcolor: 'primary.main', color: 'white' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{selected.nombre}</Typography>
                    {selected.descripcion && <Typography variant="body2" sx={{ opacity: 0.85 }}>{selected.descripcion}</Typography>}
                    {selected.responsable_nombre && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption">{selected.responsable_nombre}</Typography>
                      </Box>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    {selected.estado === 'ACTIVA' && (
                      <>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={abrirEditar} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,.2)' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cerrar caja">
                          <IconButton size="small" onClick={() => setDlgCerrar({ open: true })} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,.2)' } }}>
                            <LockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </Box>

                {/* Saldo grande */}
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>SALDO ACTUAL</Typography>
                  <Typography variant="h3" fontWeight={800}>S/ {fmt(selected.saldo_actual)}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>de S/ {fmt(selected.fondo_asignado)} asignados ({selected.moneda})</Typography>
                </Box>

                {selected.estado !== 'CERRADA' && (
                  <LinearProgress variant="determinate"
                    value={selected.fondo_asignado > 0 ? Math.min(100, (selected.saldo_actual / selected.fondo_asignado) * 100) : 0}
                    sx={{ borderRadius: 4, height: 6, bgcolor: 'rgba(255,255,255,.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} />
                )}

                {selected.umbral_reposicion != null && Number(selected.saldo_actual) <= Number(selected.umbral_reposicion) && (
                  <Alert severity="warning" sx={{ mt: 1.5, py: 0 }} icon={<WarningAmberIcon fontSize="small" />}>
                    Saldo bajo — se recomienda reponer (umbral: S/ {fmt(selected.umbral_reposicion)})
                  </Alert>
                )}
              </Box>

              {/* Acciones rápidas */}
              {selected.estado === 'ACTIVA' && (
                <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50', display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Button variant="contained" color="error" size="small" startIcon={<ArrowDownwardIcon />} onClick={abrirEgreso}>
                    Registrar Egreso
                  </Button>
                  <Button variant="contained" color="success" size="small" startIcon={<ArrowUpwardIcon />} onClick={abrirReponer}>
                    Reponer Fondo
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<BalanceIcon />} onClick={abrirArqueo}>
                    Arqueo
                  </Button>
                </Box>
              )}

              {/* Tabs contenido */}
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><HistoryIcon fontSize="small" />Movimientos <Chip size="small" label={selected.movimientos?.length || 0} sx={{ ml: 0.5, height: 18, fontSize: 10 }} /></Box>} />
                <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><BalanceIcon fontSize="small" />Arqueos</Box>} />
              </Tabs>

              {loadingDetalle ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
              ) : tab === 0 ? (
                // MOVIMIENTOS
                <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                  {(!selected.movimientos || selected.movimientos.length === 0) ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">Sin movimientos</Typography>
                    </Box>
                  ) : (
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Concepto</TableCell>
                          <TableCell align="right">Monto</TableCell>
                          <TableCell align="right">Saldo</TableCell>
                          <TableCell align="center" sx={{ width: 40 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selected.movimientos.map(m => {
                          const tc = TIPO_COLOR[m.tipo] || TIPO_COLOR.AJUSTE;
                          const canDel = selected.estado === 'ACTIVA' && !['APERTURA', 'CIERRE'].includes(m.tipo);
                          return (
                            <TableRow key={m.id} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CalendarTodayIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                                  <Typography variant="caption">{fmtDate(m.fecha_movimiento)}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label={tc.label} size="small" sx={{ bgcolor: tc.bg, color: tc.color, fontWeight: 600, fontSize: 11 }} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{m.concepto}</Typography>
                                {m.categoria && <Typography variant="caption" color="text.disabled" noWrap>{m.categoria}</Typography>}
                                {m.nro_comprobante && <Typography variant="caption" color="text.disabled" display="block">#{m.nro_comprobante}</Typography>}
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={600}
                                  color={m.tipo === 'EGRESO' ? 'error.main' : m.tipo === 'REPOSICION' || m.tipo === 'APERTURA' ? 'success.main' : 'text.primary'}>
                                  {m.tipo === 'EGRESO' ? '-' : m.tipo === 'REPOSICION' || m.tipo === 'APERTURA' ? '+' : ''}
                                  S/ {fmt(m.monto)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="caption" color="text.secondary">S/ {fmt(m.saldo_posterior)}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                {canDel && (
                                  <Tooltip title="Eliminar">
                                    <IconButton size="small" color="error" onClick={() => setDlgDelMov({ open: true, mov: m })}>
                                      <DeleteIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              ) : (
                // ARQUEOS
                <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                  {(!selected.arqueos || selected.arqueos.length === 0) ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">Sin arqueos registrados</Typography>
                      {selected.estado === 'ACTIVA' && (
                        <Button variant="outlined" size="small" startIcon={<BalanceIcon />} onClick={abrirArqueo} sx={{ mt: 1 }}>
                          Realizar primer arqueo
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Fecha</TableCell>
                          <TableCell align="right">Contable</TableCell>
                          <TableCell align="right">Físico</TableCell>
                          <TableCell align="right">Diferencia</TableCell>
                          <TableCell>Usuario</TableCell>
                          <TableCell>Obs.</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selected.arqueos.map(a => {
                          const dif = Number(a.diferencia);
                          return (
                            <TableRow key={a.id} hover>
                              <TableCell><Typography variant="caption">{fmtDate(a.fecha_arqueo)}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="caption">S/ {fmt(a.saldo_contable)}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="caption">S/ {fmt(a.saldo_fisico)}</Typography></TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                  {Math.abs(dif) < 0.01
                                    ? <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                    : <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />}
                                  <Typography variant="caption"
                                    color={Math.abs(dif) < 0.01 ? 'success.main' : dif > 0 ? 'success.main' : 'error.main'}
                                    fontWeight={600}>
                                    {dif >= 0 ? '+' : ''}S/ {fmt(dif)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell><Typography variant="caption">{a.usuario_arqueo}</Typography></TableCell>
                              <TableCell><Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>{a.observaciones}</Typography></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* ─── Dialog: Crear / Editar Caja ─── */}
      <Dialog open={dlgCaja.open} onClose={() => setDlgCaja({ ...dlgCaja, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalAtmIcon color="primary" />
          {dlgCaja.modo === 'crear' ? 'Nueva Caja Chica' : 'Editar Caja Chica'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre *" value={formCaja.nombre} onChange={e => setFormCaja({ ...formCaja, nombre: e.target.value })} fullWidth />
            <TextField label="Descripción" value={formCaja.descripcion} onChange={e => setFormCaja({ ...formCaja, descripcion: e.target.value })} fullWidth />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Moneda" select value={formCaja.moneda} onChange={e => setFormCaja({ ...formCaja, moneda: e.target.value })} fullWidth disabled={dlgCaja.modo === 'editar'}>
                  {['PEN', 'USD', 'EUR'].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Fondo Asignado *" type="number" value={formCaja.fondo_asignado}
                  onChange={e => setFormCaja({ ...formCaja, fondo_asignado: e.target.value })} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }}
                  disabled={dlgCaja.modo === 'editar'} />
              </Grid>
            </Grid>
            <TextField label="Responsable" value={formCaja.responsable_nombre}
              onChange={e => setFormCaja({ ...formCaja, responsable_nombre: e.target.value })} fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment> }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Umbral de Reposición" type="number" value={formCaja.umbral_reposicion}
                  onChange={e => setFormCaja({ ...formCaja, umbral_reposicion: e.target.value })} fullWidth
                  helperText="Alerta cuando el saldo baje de este monto"
                  InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Fecha de Apertura *" type="date" value={formCaja.fecha_apertura}
                  onChange={e => setFormCaja({ ...formCaja, fecha_apertura: e.target.value })} fullWidth
                  InputLabelProps={{ shrink: true }} disabled={dlgCaja.modo === 'editar'} />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgCaja({ ...dlgCaja, open: false })}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardarCaja} disabled={saving || !formCaja.nombre || !formCaja.fondo_asignado}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            {dlgCaja.modo === 'crear' ? 'Crear Caja' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog: Registrar Egreso ─── */}
      <Dialog open={dlgEgreso.open} onClose={() => setDlgEgreso({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArrowDownwardIcon color="error" /> Registrar Egreso
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
              Saldo disponible: <strong>S/ {fmt(selected.saldo_actual)}</strong>
            </Alert>
          )}
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Monto *" type="number" value={formEgreso.monto}
                  onChange={e => setFormEgreso({ ...formEgreso, monto: e.target.value })} fullWidth autoFocus
                  InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Fecha *" type="date" value={formEgreso.fecha_movimiento}
                  onChange={e => setFormEgreso({ ...formEgreso, fecha_movimiento: e.target.value })} fullWidth
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
            <TextField label="Concepto *" value={formEgreso.concepto}
              onChange={e => setFormEgreso({ ...formEgreso, concepto: e.target.value })} fullWidth />
            <TextField label="Categoría" select value={formEgreso.categoria}
              onChange={e => setFormEgreso({ ...formEgreso, categoria: e.target.value })} fullWidth>
              <MenuItem value=""><em>Sin categoría</em></MenuItem>
              {CATEGORIAS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Nº Comprobante" value={formEgreso.nro_comprobante}
                  onChange={e => setFormEgreso({ ...formEgreso, nro_comprobante: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Proveedor / Beneficiario" value={formEgreso.proveedor}
                  onChange={e => setFormEgreso({ ...formEgreso, proveedor: e.target.value })} fullWidth />
              </Grid>
            </Grid>
            <TextField label="Observaciones" value={formEgreso.observaciones}
              onChange={e => setFormEgreso({ ...formEgreso, observaciones: e.target.value })} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgEgreso({ open: false })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => handleMovimiento('EGRESO', formEgreso)}
            disabled={saving || !formEgreso.monto || !formEgreso.concepto}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : <ArrowDownwardIcon sx={{ mr: 0.5 }} />}
            Registrar Egreso
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog: Reponer Fondo ─── */}
      <Dialog open={dlgReponer.open} onClose={() => setDlgReponer({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArrowUpwardIcon color="success" /> Reponer Fondo
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
              Saldo actual: <strong>S/ {fmt(selected.saldo_actual)}</strong> — Fondo asignado: <strong>S/ {fmt(selected.fondo_asignado)}</strong>
            </Alert>
          )}
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Monto a Reponer *" type="number" value={formReponer.monto}
                  onChange={e => setFormReponer({ ...formReponer, monto: e.target.value })} fullWidth autoFocus
                  InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Fecha *" type="date" value={formReponer.fecha_movimiento}
                  onChange={e => setFormReponer({ ...formReponer, fecha_movimiento: e.target.value })} fullWidth
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
            <TextField label="Concepto *" value={formReponer.concepto}
              onChange={e => setFormReponer({ ...formReponer, concepto: e.target.value })} fullWidth />
            <TextField label="Observaciones" value={formReponer.observaciones}
              onChange={e => setFormReponer({ ...formReponer, observaciones: e.target.value })} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgReponer({ open: false })}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={() => handleMovimiento('REPOSICION', formReponer)}
            disabled={saving || !formReponer.monto || !formReponer.concepto}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : <ArrowUpwardIcon sx={{ mr: 0.5 }} />}
            Reponer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog: Arqueo ─── */}
      <Dialog open={dlgArqueo.open} onClose={() => setDlgArqueo({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BalanceIcon color="primary" /> Arqueo de Caja
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
              Saldo contable en sistema: <strong>S/ {fmt(selected.saldo_actual)}</strong>
            </Alert>
          )}
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Saldo Físico Contado *" type="number" value={formArqueo.saldo_fisico}
                  onChange={e => setFormArqueo({ ...formArqueo, saldo_fisico: e.target.value })} fullWidth autoFocus
                  InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }}
                  helperText="Efectivo real contado en caja" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Fecha Arqueo *" type="date" value={formArqueo.fecha_arqueo}
                  onChange={e => setFormArqueo({ ...formArqueo, fecha_arqueo: e.target.value })} fullWidth
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
            {formArqueo.saldo_fisico !== '' && selected && (
              <Alert severity={Math.abs(Number(formArqueo.saldo_fisico) - Number(selected.saldo_actual)) < 0.01 ? 'success' : 'warning'}>
                Diferencia: <strong>S/ {fmt(Number(formArqueo.saldo_fisico) - Number(selected.saldo_actual))}</strong>
                {Math.abs(Number(formArqueo.saldo_fisico) - Number(selected.saldo_actual)) > 0.009 &&
                  ' — Se registrará un ajuste automático'}
              </Alert>
            )}
            <TextField label="Observaciones" value={formArqueo.observaciones}
              onChange={e => setFormArqueo({ ...formArqueo, observaciones: e.target.value })} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgArqueo({ open: false })}>Cancelar</Button>
          <Button variant="contained" onClick={handleArqueo} disabled={saving || !formArqueo.saldo_fisico}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : <BalanceIcon sx={{ mr: 0.5 }} />}
            Confirmar Arqueo
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog: Cerrar Caja ─── */}
      <Dialog open={dlgCerrar.open} onClose={() => setDlgCerrar({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <LockIcon color="error" /> Cerrar Caja Chica
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
            Esta acción cerrará la caja permanentemente. El saldo restante (<strong>S/ {fmt(selected?.saldo_actual)}</strong>) será registrado como egreso de cierre.
          </Alert>
          <Typography variant="body2" color="text.secondary">¿Confirma el cierre de <strong>{selected?.nombre}</strong>?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgCerrar({ open: false })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleCerrar} disabled={saving}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : <LockIcon sx={{ mr: 0.5 }} />}
            Cerrar Caja
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog: Confirmar eliminar movimiento ─── */}
      <Dialog open={dlgDelMov.open} onClose={() => setDlgDelMov({ open: false, mov: null })} maxWidth="xs">
        <DialogTitle>Eliminar Movimiento</DialogTitle>
        <DialogContent>
          <Typography>¿Eliminar el movimiento <strong>{dlgDelMov.mov?.concepto}</strong> por <strong>S/ {fmt(dlgDelMov.mov?.monto)}</strong>?</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            El saldo de la caja se ajustará automáticamente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgDelMov({ open: false, mov: null })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleEliminarMov}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog: Exportar Excel ─── */}
      <Dialog open={dlgExport} onClose={() => setDlgExport(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileDownloadIcon color="success" /> Exportar Reporte Excel
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Fecha Desde *" type="date" value={exportForm.desde}
                  onChange={e => setExportForm({ ...exportForm, desde: e.target.value })} fullWidth
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Fecha Hasta *" type="date" value={exportForm.hasta}
                  onChange={e => setExportForm({ ...exportForm, hasta: e.target.value })} fullWidth
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
            <TextField label="Caja" select value={exportForm.cajaId}
              onChange={e => setExportForm({ ...exportForm, cajaId: e.target.value })} fullWidth>
              <MenuItem value="">Todas las cajas</MenuItem>
              {cajas.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nombre}</MenuItem>)}
            </TextField>
            <TextField label="Tipo de Movimiento" select value={exportForm.tipo}
              onChange={e => setExportForm({ ...exportForm, tipo: e.target.value })} fullWidth>
              <MenuItem value="">Todos los tipos</MenuItem>
              {['APERTURA', 'EGRESO', 'REPOSICION', 'AJUSTE', 'CIERRE'].map(t => (
                <MenuItem key={t} value={t}>{TIPO_COLOR[t]?.label || t}</MenuItem>
              ))}
            </TextField>
            <Alert severity="info" sx={{ py: 0.5 }}>
              El reporte incluye 2 hojas: <strong>Resumen</strong> (totales por caja) y <strong>Movimientos</strong> (detalle completo con colores por tipo).
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgExport(false)}>Cancelar</Button>
          <Button variant="contained" color="success"
            startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExportExcel}
            disabled={exportLoading || !exportForm.desde || !exportForm.hasta}>
            {exportLoading ? 'Generando...' : 'Descargar Excel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
